;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/:pathArray*\",\"regex\":\"^/(.+)$\",\"paramNames\":[\"pathArray\"],\"hasOptionalPrefix\":false},{\"path\":\"/customer/:pathArray*\",\"regex\":\"^/customer/(.+)$\",\"paramNames\":[\"pathArray\"],\"hasOptionalPrefix\":false},{\"path\":\"/swatches/swatch/:fabricName*\",\"regex\":\"^/swatches/swatch/(.+)$\",\"paramNames\":[\"fabricName\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/app-classic\"},{\"path\":\"/app-new\"},{\"path\":\"/swatches\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.0.9";import * as Sentry from '@sentry/nextjs';

import getEnvValue from 'commons/getEnvValue';

// Load public environment variables (populate globalThis._env_)
import './publicEnvConfig';

import sharedSentryConfig from '../sentry.shared-config';
import initDash0WebSdk from './initDash0WebSdk';
import { logInfo } from 'commons/logger';

const isLocal = getEnvValue('NEXT_PUBLIC_ENV_NAME') === 'local';

// Sticky per-session sampling decision shared across Sentry and Dash0.
// The Dash0 Web SDK does not expose a sample-rate option, so we sample by
// gating its init() instead. We make the same decision for Sentry so a
// session is either fully sampled across both tools or fully dropped.
// See .github/instructions/tracing.instructions.md.
const SESSION_SAMPLE_KEY = '__joybird_trace_sampled';

const computeSessionSampleDecision = sampleRate => {
  try {
    const stored = window.sessionStorage.getItem(SESSION_SAMPLE_KEY);
    if (stored === '1') return true;
    if (stored === '0') return false;
    const decision = Math.random() < sampleRate;
    window.sessionStorage.setItem(SESSION_SAMPLE_KEY, decision ? '1' : '0');
    return decision;
  } catch {
    return Math.random() < sampleRate;
  }
};

const tracingSampleRate = parseFloat(
  getEnvValue('NEXT_PUBLIC_TRACING_SAMPLE_RATE') || '0.001'
);
const isTraceSampled = computeSessionSampleDecision(tracingSampleRate);

// Initialize Sentry on the client
// https://docs.sentry.io/platforms/javascript/guides/nextjs/manual-setup/
Sentry.init({
  ...sharedSentryConfig,

  // Override the shared tracesSampleRate with a per-session decision so the
  // sampling outcome agrees with the Dash0 init gate below. Sentry uses
  // tracesSampler in preference to tracesSampleRate when both are present.
  tracesSampler: () => (isTraceSampled ? 1 : 0),

  // Proxy envelopes through our server to bypass ad blockers and avoid CORS issues.
  // Only needed locally — in deployed environments Sentry requests go direct.
  ...(isLocal && { tunnel: '/api/sentry-tunnel/' }),

  // Client-specific integrations
  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      mask: ['.sensitive', '[data-sensitive]'],
    }),
    Sentry.browserTracingIntegration({
      enableLongTask: true,
      enableNavigationSpan: true,
    }),
  ],

  // Replay configuration - configurable via environment variables.
  // Note: vars must be prefixed NEXT_PUBLIC_ so they reach the client via
  // globalThis._env_ (see packages/consumer/src/getPublicEnvVars.mjs).
  replaysSessionSampleRate: parseFloat(
    getEnvValue('NEXT_PUBLIC_SENTRY_REPLAYS_SESSION_SAMPLE_RATE') || '0.001'
  ),
  replaysOnErrorSampleRate: parseFloat(
    getEnvValue('NEXT_PUBLIC_SENTRY_REPLAYS_ERROR_SAMPLE_RATE') || '0.001'
  ),

  // Trace propagation targets - match the same domains as initDash0WebSdk propagators.
  // Exclude third-party analytics subdomains starting with 'gtm' or 'rudr'
  // (e.g. gtm.prod.nest.joybird.com) to prevent CORS errors.
  // [^/?#]* restricts matching to the hostname only, preventing false positives
  // when joybird.com appears in query parameters of third-party URLs.
  tracePropagationTargets: [
    /^https:\/\/(?!(?:gtm|rudr))[^/?#]*\.joybird\.com/,
    /^https?:\/\/[^/?#]*\.local/,
    /^https?:\/\/localhost/,
    getEnvValue('NEXT_PUBLIC_GRAPHQL_BASE_URL'),
    process.env.PRIVATE_GRAPHQL_BASE_URL,
  ].filter(Boolean),

  // Client-specific error handling
  beforeSend(event, hint) {
    // Call shared beforeSend first
    const filteredEvent = sharedSentryConfig.beforeSend?.(event, hint);
    if (!filteredEvent) return null;

    // Additional client-side filtering
    if (event.exception) {
      const error = hint.originalException;
      if (error?.message) {
        const message = error.message;
        if (
          message.includes('ResizeObserver loop limit exceeded') ||
          message.includes('Non-Error promise rejection')
        ) {
          return null;
        }
      }
    }
    return filteredEvent;
  },
});

const isClientTracingEnabled =
  getEnvValue('NEXT_PUBLIC_CLIENT_TRACING_ENABLED') === 'true';

if (isClientTracingEnabled && isTraceSampled) {
  logInfo('Initializing Dash0 Web SDK on client');
  initDash0WebSdk();
} else if (!isClientTracingEnabled) {
  logInfo('Dash0 Web SDK is disabled');
} else {
  logInfo(
    `Dash0 Web SDK skipped for this session (sample rate ${tracingSampleRate})`
  );
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
